import React, { useEffect } from 'react';
import { useI18n } from '../i18n/LanguageContext';

// Optimized printable brochure. Auto-triggers print dialog when ?print=1
export default function Brochure() {
  const { t, lang } = useI18n();

  useEffect(() => {
    document.body.classList.add('brochure-active');
    return () => document.body.classList.remove('brochure-active');
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('print') === '1') {
      const timer = setTimeout(() => window.print(), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="brochure mx-auto bg-white text-forest font-serif" data-testid="brochure-page">
      {/* Print controls (hidden on print) */}
      <div className="print:hidden bg-sand-warm border-b border-forest/15 py-4">
        <div className="max-w-[820px] mx-auto px-6 flex items-center justify-between gap-4">
          <p className="text-[12px] uppercase tracking-[0.2em] text-forest/70">
            Gravats Maresme — Brochure ({lang.toUpperCase()})
          </p>
          <button
            type="button"
            onClick={() => window.print()}
            className="btn-primary !py-3 !px-6"
            data-testid="brochure-print-button"
          >
            Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-[820px] mx-auto px-12 py-16 print:px-0 print:py-0">
        {/* ============ COVER ============ */}
        <section className="brochure-page" data-testid="brochure-cover">
          <div className="flex items-baseline justify-between border-b border-forest/30 pb-4">
            <span className="font-serif text-lg">Gravats <i className="font-light">Maresme</i></span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-forest/55">Est. 1981</span>
          </div>

          <div className="mt-32">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rust">{t.home.kicker}</p>
            <h1 className="mt-6 text-5xl leading-[1.05] font-light tracking-tight text-balance">
              {t.home.heroTitle}
            </h1>
            <p className="mt-8 max-w-xl font-sans text-base leading-relaxed text-forest/75">
              {t.home.heroSub}
            </p>
          </div>

          <div className="mt-32 grid grid-cols-4 gap-4 border-t border-forest/20 pt-6">
            {t.home.stats.map((s, i) => (
              <div key={i}>
                <div className="font-serif text-3xl font-light">{s.num}</div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-forest/60 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-20 text-[10px] uppercase tracking-[0.22em] text-forest/55 flex justify-between">
            <span>C/ Manuel Ventura Campeny, 8 · Vilassar de Dalt · Barcelona</span>
            <span>gravatsmaresme.com</span>
          </div>
        </section>

        {/* ============ ABOUT ============ */}
        <section className="brochure-page" data-testid="brochure-about">
          <BrochureHeader kicker={t.about.kicker} number="01" />
          <h2 className="mt-6 text-3xl font-light leading-[1.1] text-balance">{t.about.title}</h2>
          <p className="mt-6 lead-print">{t.about.intro}</p>

          <h3 className="mt-12 text-base uppercase tracking-[0.18em] font-sans font-medium text-rust">
            {t.about.timelineTitle}
          </h3>
          <div className="mt-6 border-t border-forest/20">
            {t.about.timeline.map((row, i) => (
              <div key={i} className="grid grid-cols-12 gap-4 py-4 border-b border-forest/15 break-inside-avoid">
                <div className="col-span-2 font-serif text-xl">{row.year}</div>
                <div className="col-span-4 font-serif text-base">{row.title}</div>
                <div className="col-span-6 font-sans text-[12px] leading-relaxed text-forest/80">{row.body}</div>
              </div>
            ))}
          </div>

          <h3 className="mt-10 text-base uppercase tracking-[0.18em] font-sans font-medium text-rust">
            {t.about.facilitiesKicker}
          </h3>
          <h4 className="mt-3 font-serif text-xl">{t.about.facilitiesTitle}</h4>
          <p className="mt-3 lead-print">{t.about.facilitiesBody}</p>
        </section>

        {/* ============ SERVICES ============ */}
        <section className="brochure-page" data-testid="brochure-services">
          <BrochureHeader kicker={t.services.kicker} number="02" />
          <h2 className="mt-6 text-3xl font-light leading-[1.1] text-balance">{t.services.title}</h2>
          <p className="mt-6 lead-print">{t.services.intro}</p>

          <div className="mt-8 border-t border-forest/20">
            {t.services.items.map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-5 py-5 border-b border-forest/15 break-inside-avoid">
                <div className="col-span-1 font-serif text-rust text-base">{item.n}</div>
                <div className="col-span-4 font-serif text-base">{item.title}</div>
                <div className="col-span-7 font-sans text-[12px] leading-relaxed text-forest/80">{item.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ============ INTERNATIONAL ============ */}
        <section className="brochure-page" data-testid="brochure-international">
          <BrochureHeader kicker={t.international.kicker} number="03" />
          <h2 className="mt-6 text-3xl font-light leading-[1.1] text-balance">{t.international.title}</h2>
          <p className="mt-6 lead-print">{t.international.intro}</p>

          <div className="mt-10 grid grid-cols-2 gap-x-10 gap-y-6">
            {t.international.regions.map((r, i) => (
              <div key={i} className="break-inside-avoid">
                <div className="text-[10px] uppercase tracking-[0.22em] text-rust">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h4 className="mt-1 font-serif text-xl">{r.name}</h4>
                <p className="mt-2 font-sans text-[12px] leading-relaxed text-forest/80">{r.body}</p>
              </div>
            ))}
          </div>

          <h3 className="mt-12 text-base uppercase tracking-[0.18em] font-sans font-medium text-rust">
            {t.international.capabilitiesTitle}
          </h3>
          <ul className="mt-4 space-y-3">
            {t.international.capabilities.map((c, i) => (
              <li key={i} className="flex gap-3 font-sans text-[12px] leading-relaxed text-forest/85">
                <span className="font-serif text-rust">— {String(i + 1).padStart(2, '0')}</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ============ SUSTAINABILITY ============ */}
        <section className="brochure-page" data-testid="brochure-sustainability">
          <BrochureHeader kicker={t.sustainability.kicker} number="04" />
          <h2 className="mt-6 text-3xl font-light leading-[1.1] text-balance">{t.sustainability.title}</h2>
          <p className="mt-6 lead-print">{t.sustainability.intro}</p>

          <div className="mt-10 space-y-8">
            {t.sustainability.projects.map((p, i) => (
              <div key={i} className="break-inside-avoid border-t border-forest/20 pt-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-rust">{p.code}</div>
                <h3 className="mt-2 font-serif text-2xl">{p.name}</h3>
                <p className="mt-3 font-sans text-[12px] leading-relaxed text-forest/80">{p.body}</p>
                <ul className="mt-4 grid grid-cols-2 gap-x-8 gap-y-2">
                  {p.benefits.map((b, k) => (
                    <li key={k} className="flex gap-2 font-sans text-[11px] leading-relaxed text-forest/85">
                      <span className="text-rust">·</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="mt-10 font-sans text-[11px] italic leading-relaxed text-forest/65">
            {t.sustainability.partnership}
          </p>
        </section>

        {/* ============ CONTACT ============ */}
        <section className="brochure-page" data-testid="brochure-contact">
          <BrochureHeader kicker={t.contact.kicker} number="05" />
          <h2 className="mt-6 text-3xl font-light leading-[1.1] text-balance">{t.contact.title}</h2>
          <p className="mt-6 lead-print">{t.contact.intro}</p>

          <div className="mt-12 grid grid-cols-2 gap-x-10 gap-y-8 border-t border-forest/20 pt-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-forest/55">{t.contact.info.addressLabel}</div>
              <p className="mt-2 font-serif text-base whitespace-pre-line leading-snug">{t.contact.info.address}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-forest/55">{t.contact.info.phoneLabel}</div>
              <p className="mt-2 font-serif text-base">+34 93 753 26 12<br />+34 615 198 057</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-forest/55">{t.contact.info.emailLabel}</div>
              <p className="mt-2 font-serif text-base">info@gravatsmaresme.com</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-forest/55">{t.contact.info.hoursLabel}</div>
              <p className="mt-2 font-serif text-base">{t.contact.info.hours}</p>
            </div>
          </div>

          <div className="mt-32 border-t border-forest/20 pt-6 flex items-baseline justify-between">
            <span className="font-serif italic text-base text-forest/65">Gravats Maresme, S.L.</span>
            <span className="text-[9px] uppercase tracking-[0.25em] text-forest/45">© {new Date().getFullYear()} · gravatsmaresme.com</span>
          </div>
        </section>
      </div>
    </div>
  );
}

function BrochureHeader({ kicker, number }) {
  return (
    <div className="flex items-baseline justify-between border-b border-forest/30 pb-3">
      <span className="text-[10px] uppercase tracking-[0.25em] text-rust">{kicker}</span>
      <span className="font-serif text-base text-forest/45">— {number}</span>
    </div>
  );
}
